import React, { useEffect, useState } from 'react';
import Modal from '../../../components/ModalComponents/Modal';
import { createTechnology, getAllTechnologies, updateTechnology } from '../../../api/sales';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import MyTable from '../../../components/MyTable';
import FilterPopup from '../../../components/FilterPopup';
import TechnologyHeader from './components/TechnologyHeader';
import { formatDateCST, formatTimeCST } from '../../../utlis';
const Technologies = () => {
  const initialTechnology = {
    technologyName: '',
    terminology: '',
    description: '',
    createdDate: '',
    createdTime: '',
    updateDate: '',
    updateTime: '',
    created_by: '',
    updated_by: '',
  };
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, dirtyFields },
  } = useForm();
  // Helper to compare form data with original record
  const isFormChanged = () => {
    if (!technologyId) return true; // Always enabled for add
    const watched = watch();
    // Only compare editable fields
    return (
      watched.technologyName !== technology.technologyName ||
      watched.terminology !== technology.terminology ||
      watched.description !== technology.description
    );
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterPopupOpen, setFilterPopupOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [technology, setTechnology] = useState(initialTechnology);
  const [technologyId, setTechnologyId] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setLoading] = useState(true); // start loading immediately
  const [networkError, setNetworkError] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Filter data based on search term
  const getSearchFilteredData = (dataToFilter) => {
    if (!searchTerm) return dataToFilter;

    const searchLower = searchTerm.toLowerCase();
    return dataToFilter.filter((item) =>
      (item.technology_name || '').toLowerCase().includes(searchLower) ||
      (item.description || '').toLowerCase().includes(searchLower) ||
      (item.terminology?.test || '').toLowerCase().includes(searchLower)
    );
  };
  const getData = async (showToast = false) => {
    setLoading(true);
    setNetworkError(false);
    try {
      const res = await getAllTechnologies();
      const records = res?.data?.data?.records;
      if (Array.isArray(records)) {
        setData(records);
      } else {
        setData([]);
      }
      // Reset filters when refreshing data
      setAppliedFilters([]);
      setFilteredData([]);
      if (showToast) {
        toast.success('Data refreshed successfully', { toastId: 'refresh-success' });
      }
    } catch (err) {
      setNetworkError(true);
      setData([]);
      toast.error('Network error: Unable to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to the data
  useEffect(() => {
    if (!data.length) return;

    if (Array.isArray(appliedFilters) && appliedFilters.length > 0) {
      const filtered = data.filter((item) => {
        return appliedFilters.some(({ category, items }) => {
          if (!items || items.length === 0) return false;

          switch (category) {
            case 'Technology ID':
              return items.some((id) => id === item.technology_id);
            case 'Technology Name':
              return items.some((name) => name === item.technology_name);
            case 'Description':
              return items.some((desc) => desc === item.description);
            case 'Terminology':
              return items.some((term) => term === item.terminology?.test);
            default:
              return false;
          }
        });
      });

      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [appliedFilters, data]);

  useEffect(() => {
    getData(); // No toast on initial load
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    reset(initialTechnology);
    setTechnology(initialTechnology);
    setTechnologyId(null);
  };

  const onSubmit = async (formData) => {
    setLoading(true);
    try {
      if (technologyId) {
        await updateTechnology({ ...formData, technologyId });
        toast.success('Technology Updated Successfully');
      } else {
        await createTechnology(formData);
        toast.success('Technology Created Successfully');
      }
      getData();
      closeModal();
    } catch (err) {
      console.log(err);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const prepareUpdate = (technology_id) => {
    const TechnologyData = data.find((d) => d.technology_id === technology_id);
    console.log(TechnologyData);
    if (!TechnologyData) {
      toast.error('Technology not found');
      return;
    }
    const createdDate = new Date(TechnologyData.created);
    const updatedDate = new Date(TechnologyData.updated);
    reset({
      technologyName: TechnologyData.technology_name,
      terminology: TechnologyData.terminology?.test,
      description: TechnologyData.description,
      createdDate: createdDate.toISOString().split('T')[0] || ' ',
      createdTime: createdDate.toISOString().split('T')[1].slice(0, 5) || ' ',
      updateDate: updatedDate.toISOString().split('T')[0] || ' ',
      updateTime: updatedDate.toISOString().split('T')[1].slice(0, 5) || ' ',
      created_by: TechnologyData.created_by,
      updated_by: TechnologyData.updated_by,
    });
    setTechnology({
      technologyName: TechnologyData.technology_name,
      terminology: TechnologyData.terminology?.test,
      description: TechnologyData.description,
      // Audit fields - store raw timestamps for formatting
      created: TechnologyData.created || '',
      created_by: TechnologyData.created_by || '',
      updated: TechnologyData.updated || '',
      updated_by: TechnologyData.updated_by || '',
    });
    setTechnologyId(TechnologyData.technology_id);
    openModal();
  };
  const columns = [
    {
      name: 'technology_id',
      label: 'Technology ID',
      options: {
        display: 'false',
      },
    },
    {
      name: 'technology_name',
      label: 'Technology Name',
      options: {
        customBodyRender: (value, tableMeta) => {
          const handleClick = (e) => {
            e.stopPropagation(); // Prevent row click from triggering
            const technology_id = tableMeta.rowData[0]; // Get technology_id from first column
            prepareUpdate(technology_id);
          };

          return (
            <h3
              className="text-base font-bold text-auxiliary-800 cursor-pointer hover:text-secondary-700"
              onClick={handleClick}
            >
              {value}
            </h3>
          );
        },
      },
    },
    {
      name: 'description',
      label: 'Description',
    },
    {
      name: 'terminology',
      label: 'Terminology',
      options: {
        customBodyRender: (value) => value?.test || '-',
      },
    },
    // Types column removed
  ];
  const handleFilterClick = () => {
    setFilterPopupOpen(true);
  };

  const handleClosePopup = () => {
    setFilterPopupOpen(false);
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters || []);
    setFilterPopupOpen(false);
  };

  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsPerPage: pageSize,
    rowsPerPageOptions: [5, 10, 20, 50, 300, 200],
    selectableRows: 'none',
    elevation: 0,
    filter: true,
    search: false,
    download: true,
    print: true,
    viewColumns: true,
    onRowClick: (rowData) => prepareUpdate(rowData[0]),
  };
  return (
    <div className="p-5">
      {/* Loader and error handling for initial data fetch */}
      {networkError ? (
        <div className="flex items-center justify-center h-[400px]">
          <span className="bg-red-100 text-red-700 px-6 py-3 rounded text-lg font-semibold">
            Network error: Unable to fetch data
          </span>
        </div>
      ) : (
        <>
          <Modal isOpen={isModalOpen} onClose={closeModal}>
            <div className="relative">
              {/* Modal Heading */}
              <div className="flex justify-between items-center mb-6">
                <div className="w-full">
                  <h2 className="w-full text-2xl font-bold text-secondary-700 p-3">
                    {technologyId ? (
                      <>
                        <span className="text-2xl font-bold text-black">
                          Edit Technology Name{' '}
                          <span style={{ color: 'rgb(178, 120, 33)' }}>
                            - {technology.technologyName}
                          </span>
                        </span>
                      </>
                    ) : (
                      'Add Technology'
                    )}
                  </h2>
                  <hr className="w-full border-t border-gray-200 mt-2" />
                </div>
                <button
                  onClick={closeModal}
                  className="max-w-[60px] text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
                  aria-label="Close"
                  type="button"
                >
                  &times;
                </button>
              </div>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-6 text-text-hint"
              >
                <div className="grid grid-cols-3 gap-5">
                  <div className="flex flex-col w-72">
                    <label htmlFor="technology_name" className="text-text-hint mb-1">
                      Technology Name
                    </label>
                    <input
                      {...register('technologyName', {
                        required: 'Technology Name is required',
                      })}
                      name="technologyName"
                      type="text"
                      placeholder="Technology Name"
                      id="technology_name"
                      className="outline-none border-2 border-secondary-500 rounded-lg text-black pencialicon"
                    />
                    {errors.technologyName && (
                      <p className="text-red-500 text-sm">{errors.technologyName.message}</p>
                    )}
                  </div>
                  <div className="flex flex-col w-72">
                    <label htmlFor="terminology" className="text-text-hint mb-1">
                      Terminology
                    </label>
                    <input
                      {...register('terminology', {
                        required: 'Terminology is required',
                      })}
                      name="terminology"
                      type="text"
                      placeholder="Terminology"
                      id="terminology"
                      className="outline-none border-2 border-secondary-500 rounded-lg text-black pencialicon"
                    />
                    {errors.terminology && (
                      <p className="text-red-500 text-sm">{errors.terminology.message}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-5">
                  <div className="flex flex-col col-span-3 ">
                    <label htmlFor="document_type" className="text-text-hint mb-1">
                      Description
                    </label>
                    <textarea
                      {...register('description', {
                        required: 'Description is required',
                      })}
                      name="description"
                      type="text"
                      placeholder="Description"
                      id="document_type"
                      className="outline-none border-2 border-secondary-500 rounded-lg text-black pencialicon"
                      rows={2}
                    />
                    {errors.description && (
                      <p className="text-red-500 text-sm">{errors.description.message}</p>
                    )}
                  </div>
                </div>
              </form>

              {/* Audit Fields - Only show in Edit Mode */}
              {technologyId && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
                  <div className="flex flex-col space-y-1">
                    <label className="text-text-hint text-xs">Created By</label>
                    <p className="text-sm text-gray-600">{technology.created_by || 'N/A'}</p>
                  </div>
                  <div className="flex flex-col space-y-1">
                    <label className="text-text-hint text-xs">Date Created</label>
                    <p className="text-sm text-gray-600">
                      {technology.created ? formatDateCST(technology.created) : 'N/A'}
                    </p>
                    {technology.created && (
                      <p className="text-xs text-gray-500">{formatTimeCST(technology.created)}</p>
                    )}
                  </div>
                  <div className="flex flex-col space-y-1">
                    <label className="text-text-hint text-xs">Updated By</label>
                    <p className="text-sm text-gray-600">{technology.updated_by || 'N/A'}</p>
                  </div>
                  <div className="flex flex-col space-y-1">
                    <label className="text-text-hint text-xs">Last Updated</label>
                    <p className="text-sm text-gray-600">
                      {technology.updated ? formatDateCST(technology.updated) : 'N/A'}
                    </p>
                    {technology.updated && (
                      <p className="text-xs text-gray-500">{formatTimeCST(technology.updated)}</p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex justify-between p-6 mt-6">
                <button
                  onClick={closeModal}
                  className="border border-secondary-800 text-secondary-800 py-2 px-5 rounded-full"
                >
                  Close
                </button>
                <button
                  onClick={handleSubmit(onSubmit)}
                  className={`bg-secondary-700 text-text-light py-2 px-5 rounded-full ${
                    !isFormChanged() || Object.keys(errors).length > 0
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                  disabled={!isFormChanged() || Object.keys(errors).length > 0}
                >
                  {technologyId ? 'Update Details' : 'Add Technologies'}
                </button>
              </div>
            </div>
          </Modal>
          <div className="border border-gray-300 rounded-lg relative">
            {/* Header */}
            <TechnologyHeader
              onReload={() => getData(true)}
              onAddTechnology={openModal}
              isLoading={isLoading}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
            />

            {/* Table with loading overlay */}
            <div className="relative">
              <MyTable
                title=""
                data={
                  isLoading
                    ? []
                    : getSearchFilteredData(
                        Array.isArray(appliedFilters) && appliedFilters.length > 0
                          ? filteredData
                          : data
                      )
                }
                columns={columns}
                options={{
                  ...options,
                  textLabels: {
                    body: {
                      noMatch: isLoading ? '' : 'Sorry, no matching data found',
                    },
                  },
                }}
                onFilterClick={handleFilterClick}
              />
              {isLoading && (
                <div
                  className="absolute left-0 right-0 flex flex-col items-center justify-center z-20"
                  style={{ top: '64px', bottom: 0, background: 'rgba(255,255,255,0.9)' }}
                >
                  {/* Spinner */}
                  <div className="w-10 h-10 border-4 border-secondary-200 border-t-secondary-700 rounded-full animate-spin mb-3"></div>
                  {/* Loading text with animated dots */}
                  <span className="text-lg text-secondary-700 font-semibold flex items-center">
                    Loading
                    <span className="inline-flex ml-1">
                      <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                      <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                      <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>
          {isFilterPopupOpen && (
            <FilterPopup
              onClose={handleClosePopup}
              onApply={handleApplyFilters}
              tableData={data}
              categories={['Technology ID', 'Technology Name', 'Description', 'Terminology']}
              getSearchResults={(searchTerm, selectedCategory) => {
                if (!searchTerm || !selectedCategory || !data) return [];

                return data
                  .map((item) => {
                    switch (selectedCategory) {
                      case 'Technology ID':
                        return item.technology_id?.toString();
                      case 'Technology Name':
                        return item.technology_name;
                      case 'Description':
                        return item.description;
                      case 'Terminology':
                        return item.terminology?.test;
                      default:
                        return null;
                    }
                  })
                  .filter(
                    (value) => value && value.toLowerCase().includes(searchTerm.toLowerCase()),
                  );
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

export default Technologies;
